/* ============================================================================
 * auth.jsx — Authentication (ES modules)
 * ========================================================================= */

import React from 'react'
import { reportError, clearError, ERROR_CODES } from './errors.jsx'
import { getClient } from './db.jsx'

const PREVIEW_KEY = "wyf-preview-session";

function isUnconfigured() {
  const cfg = window.WYF_CONFIG;
  return !cfg || !cfg.SUPABASE_URL || !cfg.SUPABASE_ANON_KEY ||
         cfg.SUPABASE_URL.includes("YOUR-");
}

function readPreviewSession() {
  try { return JSON.parse(localStorage.getItem(PREVIEW_KEY) || "null"); }
  catch { return null; }
}

export function useAuth() {
  const [state, setState] = React.useState({ loading: true, user: null, profile: null });
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    if (isUnconfigured()) {
      const session = readPreviewSession();
      if (session) {
        setState({
          loading: false,
          user: { email: session.email, id: "preview-admin" },
          profile: { id: "preview-admin", email: session.email, role: session.role, full_name: session.full_name },
        });
      } else {
        setState({ loading: false, user: null, profile: null });
      }
      const handler = () => setTick(t => t + 1);
      window.addEventListener("wyf-preview-auth", handler);
      return () => window.removeEventListener("wyf-preview-auth", handler);
    }

    const client = getClient();
    if (!client) { setState({ loading: false, user: null, profile: null }); return; }
    let cancelled = false;
    const failsafe = setTimeout(() => {
      if (!cancelled) setState(s => s.loading ? { loading: false, user: null, profile: null } : s);
    }, 8000);

    async function loadProfile(user) {
      if (!user) return null;
      const meta    = user.user_metadata || {};
      const appMeta = user.app_metadata  || {};
      const fallback = {
        id:        user.id,
        email:     user.email,
        role:      appMeta.role || meta.role || "viewer",
        full_name: meta.full_name || meta.name || user.email,
      };
      try {
        const timer = new Promise(res => setTimeout(res, 2000));
        const query = client.from("app_users").select("*").eq("id", user.id).maybeSingle();
        const { data } = await Promise.race([query, timer.then(() => ({}))]);
        if (data) return data;
      } catch (e) {
        console.warn("[WYF] loadProfile error:", e.message);
      }
      return fallback;
    }

    client.auth.getSession().then(async ({ data }) => {
      const user = data.session?.user || null;
      const profile = await loadProfile(user);
      if (!cancelled) { clearTimeout(failsafe); setState({ loading: false, user, profile }); }
    }).catch(() => {
      if (!cancelled) { clearTimeout(failsafe); setState({ loading: false, user: null, profile: null }); }
    });

    const { data: sub } = client.auth.onAuthStateChange(async (evt, session) => {
      if (evt === "INITIAL_SESSION") return;
      const user = session?.user || null;
      const profile = await loadProfile(user);
      if (!cancelled) { clearTimeout(failsafe); setState({ loading: false, user, profile }); }
    });
    return () => { cancelled = true; clearTimeout(failsafe); sub.subscription.unsubscribe(); };
  }, [tick]);

  return state;
}

export async function signIn(email, password, captchaToken) {
  if (isUnconfigured()) {
    const preview = readPreviewSession();
    if (!preview) {
      return { error: { message: "Preview mode not configured. Set credentials in localStorage key 'wyf-preview-creds'." } };
    }
    if (email.trim().toLowerCase() === preview.email && password === preview.password) {
      localStorage.setItem(PREVIEW_KEY, JSON.stringify(preview));
      window.dispatchEvent(new Event("wyf-preview-auth"));
      return { error: null };
    }
    return { error: { message: "Invalid email or password (preview mode)." } };
  }
  const client = getClient();
  if (!client) return { error: { message: "Supabase not configured" } };
  return client.auth.signInWithPassword({ email, password,
    options: captchaToken ? { captchaToken } : undefined });
}

export async function signOut() {
  if (isUnconfigured()) {
    localStorage.removeItem(PREVIEW_KEY);
    window.dispatchEvent(new Event("wyf-preview-auth"));
    return;
  }
  const client = getClient();
  if (client) await client.auth.signOut();
}

export async function sendPasswordReset(email, captchaToken) {
  if (isUnconfigured()) {
    return { error: { message: "Configure Supabase first to use password reset." } };
  }
  const client = getClient();
  if (!client) return { error: { message: "Supabase not configured" } };
  return client.auth.resetPasswordForEmail(email, {
    redirectTo: window.WYF_CONFIG.PASSWORD_RESET_REDIRECT,
    ...(captchaToken ? { captchaToken } : {}),
  });
}

export async function updatePassword(newPassword) {
  const client = getClient();
  if (!client) return { error: { message: "Supabase not configured" } };
  return client.auth.updateUser({ password: newPassword });
}

export function LoginScreen() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [captchaToken, setCaptchaToken] = React.useState("");
  const [captchaReady, setCaptchaReady] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [showReset, setShowReset] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState("");
  const [resetMsg, setResetMsg] = React.useState("");
  const widgetId   = React.useRef(null);
  const tsContainer = React.useRef(null);

  const cfg = window.WYF_CONFIG;
  const unconfigured = isUnconfigured();
  const sitekey = cfg?.TURNSTILE_SITEKEY;
  const useTurnstile = !!sitekey;

  // Explicit render avoids timing race where auto-render fires before callbacks are wired
  React.useEffect(() => {
    if (!useTurnstile) { setCaptchaReady(true); return; }

    let live = true;
    let poll;

    function mount() {
      if (!live || !tsContainer.current || !window.turnstile) return;
      widgetId.current = window.turnstile.render(tsContainer.current, {
        sitekey,
        theme: "auto",
        callback:            (token) => { if (live) { setCaptchaToken(token); setCaptchaReady(true); } },
        "error-callback":    ()      => { if (live) setCaptchaReady(true); },
        "expired-callback":  ()      => { if (live) { setCaptchaToken(""); setCaptchaReady(false); } },
      });
    }

    if (window.turnstile) {
      mount();
    } else {
      poll = setInterval(() => { if (window.turnstile) { clearInterval(poll); mount(); } }, 200);
    }

    return () => {
      live = false;
      clearInterval(poll);
      if (widgetId.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch {}
        widgetId.current = null;
      }
      setCaptchaToken("");
      setCaptchaReady(false);
    };
  }, [useTurnstile, sitekey, showReset]);

  function resetTurnstile() {
    setCaptchaToken("");
    setCaptchaReady(false);
    if (widgetId.current != null && window.turnstile) {
      try { window.turnstile.reset(widgetId.current); } catch {}
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setBusy(true);
    const { error } = await signIn(email.trim(), password, captchaToken || null);
    setBusy(false);
    resetTurnstile();
    if (error) {
      reportError({
        code: "WYF-AUTH-001", where: "Login",
        message: ERROR_CODES["WYF-AUTH-001"], detail: error.message,
      });
      return;
    }
    clearError();
  }

  async function handleReset(e) {
    e.preventDefault();
    setResetMsg("");
    const { error } = await sendPasswordReset(resetEmail.trim(), captchaToken || null);
    resetTurnstile();
    if (error) {
      reportError({ code: "WYF-AUTH-005", where: "Password reset", message: ERROR_CODES["WYF-AUTH-005"], detail: error.message });
      return;
    }
    setResetMsg("If an account exists for that email, a reset link is on its way.");
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <img src="uploads/baguio-01.png" alt="What's Your Flan?" className="auth-logo" />
        </div>
        <h1>{cfg.APP_NAME}</h1>
        <p className="sub">{showReset ? "Reset your password" : "Sign in to manage operations"}</p>

        {!showReset && unconfigured && (
          <div className="lockout" style={{ background: "rgba(245,158,11,.1)", borderColor: "rgba(245,158,11,.5)", color: "var(--warn)" }}>
            <strong>Preview mode.</strong> Supabase is not configured yet.
            <div style={{ marginTop: 6, fontSize: 12 }}>
              To enable preview login, run in browser console:<br/>
              <code style={{ fontFamily: "var(--mono)" }}>
                localStorage.setItem("wyf-preview-creds", JSON.stringify({"{"}email:"you@example.com",password:"yourpass",full_name:"You",role:"admin"{"}"}))
              </code>
            </div>
          </div>
        )}

        {showReset ? (
          <form onSubmit={handleReset}>
            <div className="form-row">
              <label>Email</label>
              <input className="input" type="email" required value={resetEmail}
                     onChange={e => setResetEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            {resetMsg && <p className="muted" style={{ fontSize: 12 }}>{resetMsg}</p>}
            {useTurnstile && (
              <div className="form-row">
                <div ref={tsContainer} />
              </div>
            )}
            <button className="btn btn-primary btn-block" type="submit"
                    disabled={!captchaReady}>Send reset link</button>
            <div className="actions">
              <button type="button" className="small-link" onClick={() => setShowReset(false)}>← Back to sign in</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleLogin}>
            <div className="form-row">
              <label>Email</label>
              <input className="input" type="email" required autoComplete="username"
                     value={email} onChange={e => setEmail(e.target.value)}
                     placeholder="you@example.com" />
            </div>
            <div className="form-row">
              <label>Password</label>
              <input className="input" type="password" required autoComplete="current-password"
                     value={password} onChange={e => setPassword(e.target.value)} />
            </div>
            {useTurnstile && (
              <div className="form-row">
                <div ref={tsContainer} />
              </div>
            )}
            <button className="btn btn-primary btn-block" type="submit"
                    disabled={busy || !captchaReady}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <div className="actions">
              <button type="button" className="small-link" onClick={() => setShowReset(true)}>Forgot password?</button>
              <span className="muted" style={{ fontSize: 11 }}>v{cfg.BUILD_VERSION}</span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function ResetScreen() {
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw !== pw2) {
      reportError({ code: "WYF-VAL-003", where: "Reset password", message: "Passwords do not match" });
      return;
    }
    if (pw.length < 8 || !/\d/.test(pw)) {
      reportError({ code: "WYF-AUTH-006", where: "Reset password", message: ERROR_CODES["WYF-AUTH-006"] });
      return;
    }
    setBusy(true);
    const { error } = await updatePassword(pw);
    setBusy(false);
    if (error) {
      reportError({ code: "WYF-AUTH-005", where: "Reset password", message: error.message });
      return;
    }
    setDone(true);
    setTimeout(() => { window.location.search = ""; }, 1500);
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1>Set a new password</h1>
        <p className="sub">Choose a password with at least 8 characters and one number.</p>
        {done ? (
          <p className="muted">Password updated. Redirecting…</p>
        ) : (
          <form onSubmit={submit}>
            <div className="form-row">
              <label>New password</label>
              <input className="input" type="password" required value={pw}
                     onChange={e => setPw(e.target.value)} />
            </div>
            <div className="form-row">
              <label>Confirm password</label>
              <input className="input" type="password" required value={pw2}
                     onChange={e => setPw2(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export function useInactivityLogout(ms) {
  const [warning, setWarning] = React.useState(false);
  React.useEffect(() => {
    let timer, warningTimer;
    const reset = () => {
      clearTimeout(timer);
      clearTimeout(warningTimer);
      setWarning(false);
      if (ms > 60_000) warningTimer = setTimeout(() => setWarning(true), ms - 60_000);
      timer = setTimeout(() => signOut(), ms);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach(ev => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => { clearTimeout(timer); clearTimeout(warningTimer); events.forEach(ev => window.removeEventListener(ev, reset)); };
  }, [ms]);
  return warning;
}

export function WelcomeScreen() {
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function submit(e) {
    e.preventDefault();
    if (pw !== pw2) {
      reportError({ code: "WYF-VAL-003", where: "Welcome", message: "Passwords do not match" });
      return;
    }
    if (pw.length < 8 || !/\d/.test(pw)) {
      reportError({ code: "WYF-AUTH-006", where: "Welcome", message: ERROR_CODES["WYF-AUTH-006"] });
      return;
    }
    setBusy(true);
    const { error } = await updatePassword(pw);
    setBusy(false);
    if (error) {
      reportError({ code: "WYF-AUTH-005", where: "Welcome", message: error.message });
      return;
    }
    setDone(true);
    setTimeout(() => { window.location.replace(window.location.origin + "/"); }, 800);
  }

  if (done) {
    return (
      <div className="loading-screen">
        <div className="loading-inner">
          <img src="uploads/baguio-01.png" alt="Loading" className="loading-logo" />
          <span className="loading-label">Signing you in…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <img src="uploads/baguio-01.png" alt="What's Your Flan?" className="auth-logo" />
        </div>
        <h1>Welcome to WYF</h1>
        <p className="sub">Set a password to activate your account. At least 8 characters, one number.</p>
        <form onSubmit={submit}>
          <div className="form-row">
            <label>Password</label>
            <input className="input" type="password" required value={pw}
                   onChange={e => setPw(e.target.value)} />
          </div>
          <div className="form-row">
            <label>Confirm password</label>
            <input className="input" type="password" required value={pw2}
                   onChange={e => setPw2(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? "Setting password…" : "Activate account"}
          </button>
        </form>
      </div>
    </div>
  );
}

